import * as Api from '@ViewModels';
import { BoardStageDefinitionViewModel } from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import {
	invalidateBoardsQuery,
	useCreateBoardMutation,
	useDeleteBoardMutation,
	useInfiniteBoardsQuery,
	useUpdateBoardMutation,
} from '../../../../queries';
import { Dropdown, DropdownContent, DropdownEllipsisTrigger, DropdownItem } from '../../../components-v2/Dropdown';
import { Input } from '../../../components-v2/Input';
import { Page, Row } from '../../../components-v2/PageLayout';
import { EditBoardModal } from '../../../components/boards/EditBoard';
import { DeleteConfirmation } from '../../../components/DeleteConfirmation';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { MultiContainerHeader } from '../../../components/MultiContainerHeader';
import { DataboardsIcon } from '../../../components/svgs/icons/DataboardsIcon';
import { FolderIcon } from '../../../components/svgs/icons/FolderIcon';
import { HeartIcon } from '../../../components/svgs/icons/HeartIcon';
import { PolicyIcon } from '../../../components/svgs/icons/PolicyIcon';
import { white } from '../../../styles/colors';
import { baseStyleSheet, bs } from '../../../styles/styles';
import { styleSheet } from './styles';

const getInitialBoardColumns = (userSession: Api.UserSessionContext) => {
	const industry = userSession.account.toJs().additionalInfo?.industry;
	switch (industry) {
		case Api.Industry.RealEstate: {
			return ['Leads', 'Appointment Booked', 'Offer Submitted', 'Under Contract', 'Closed'];
		}
		default: {
			return ['Leads', 'Rescheduled', 'Demo Booked', 'Committed', 'Closing', 'Won', 'Lost'];
		}
	}
};

const MIN_BOARDS_FOR_SEARCH = 10;

export const DataBoardsHome = () => {
	const userSession = useUserSession();

	const { policiesEnabled, donationsEnabled, opportunitiesEnabled, matterBoard, meetingBoard } =
		userSession.account.features?.boards ?? {};

	const showDonations = donationsEnabled;
	const showPolicies = policiesEnabled;
	const showOpportunities = opportunitiesEnabled;
	const showMatters = matterBoard?.enabled;
	const showMeetings = meetingBoard?.enabled;

	const [isEditBoardOpen, setIsEditBoardOpen] = React.useState(false);
	const [boardToEdit, setBoardToEdit] = React.useState<Api.BoardViewModel>(null);
	const [boardToDelete, setBoardToDelete] = React.useState<Api.IBoard>(null);
	const [boardSearchTerm, setBoardSearchTerm] = React.useState('');
	const errorMessages = useErrorMessages();

	const boardsQuery = useInfiniteBoardsQuery({
		type: 'Opportunity',
		autoFetchAll: true,
		meta: {
			toastErrorMessage: 'Error loading boards',
		},
	});

	const createBoardMutation = useCreateBoardMutation({
		onSuccess: () => invalidateBoardsQuery(),
		onError: error => {
			errorMessages.pushApiError(error);
		},
	});
	const updateBoardMutation = useUpdateBoardMutation({
		onSuccess: () => invalidateBoardsQuery(),
		onError: error => {
			errorMessages.pushApiError(error);
		},
	});
	const deleteBoardMutation = useDeleteBoardMutation({
		onSuccess: () => invalidateBoardsQuery(),
		onError: error => {
			errorMessages.pushApiError(error);
		},
	});

	const boards = boardsQuery.data?.pages.map(page => page.values).flat() ?? [];
	const filteredBoards = boardSearchTerm
		? boards.filter(board => board.name?.toLowerCase().includes(boardSearchTerm.toLowerCase()))
		: boards;

	const editBoard = (board: Api.IBoard) => {
		setBoardToEdit(new Api.BoardViewModel(userSession, board) as unknown as Api.BoardViewModel);
		setIsEditBoardOpen(true);
	};

	const deleteBoard = (board: Api.IBoard) => {
		setBoardToDelete(board);
	};

	const createBoard = () => {
		setIsEditBoardOpen(true);
	};

	const onCreateBoard = async (name: string, columns: Api.BoardStageDefinitionViewModel[]) => {
		const newBoard: Api.IBoard = {
			name,
			stages: (columns || []).map(column => ({
				...column.toJs(),
				templateType: Api.BoardTemplateType.Opportunity,
			})),
			template: {
				type: Api.BoardTemplateType.Opportunity,
			},
		};

		const result = await createBoardMutation.mutateAsync(newBoard);

		setIsEditBoardOpen(false);
		return new Api.BoardViewModel(userSession, result);
	};

	const onEditBoard = async (board: Api.BoardViewModel, name: string, columns: BoardStageDefinitionViewModel[]) => {
		const updatedBoard: Api.IBoard = {
			...board.toJs(),
			name,
			stages: columns.map(x => x.toJs()),
		};
		const result = await updateBoardMutation.mutateAsync(updatedBoard);

		setIsEditBoardOpen(false);
		return new Api.BoardViewModel(userSession, result);
	};

	const onDeleteBoard = async (shouldDelete: boolean) => {
		if (shouldDelete) {
			await deleteBoardMutation.mutateAsync(boardToDelete.id);
		}

		setBoardToDelete(null);
	};

	const initialColumns: BoardStageDefinitionViewModel[] = getInitialBoardColumns(userSession).map(columnTitle => {
		const column = new BoardStageDefinitionViewModel();
		column.name = columnTitle;
		column.enableStageIndicator = ['Lost', 'Closed'].indexOf(columnTitle) > -1;
		return column;
	});

	const isLoadingOpportunityBoards = boardsQuery.isPending;

	return (
		<Page>
			<MultiContainerHeader
				appBarHeader={
					<Row>
						<h1 className={css(bs.breadcrumbTitle)}>Data Boards</h1>
					</Row>
				}
			/>

			{showDonations ? (
				<div className={css(styleSheet.cardsRow)}>
					<section className={css(styleSheet.boardRowHeader)}>
						<h2 className={css(styleSheet.boardTypeTitle)}>Donations Board</h2>

						<p className={css(styleSheet.description)}>Keep track of individual donations and donation amounts.</p>
					</section>

					<div className={css(styleSheet.cardsContainer)}>
						<div className={css(styleSheet.card)}>
							<Link to='/dataBoards/donations' className={css(styleSheet.boardLink)}>
								<div className={css(styleSheet.boardIconContainer, baseStyleSheet.bgPink)}>
									<HeartIcon height={24} width={24} className={css(styleSheet.heartIcon)} />
								</div>

								<h6 className={css(styleSheet.boardTitle)}>Donations</h6>
							</Link>
						</div>
					</div>
				</div>
			) : null}

			{showPolicies ? (
				<div className={css(styleSheet.cardsRow)}>
					<section className={css(styleSheet.boardRowHeader)}>
						<h2 className={css(styleSheet.boardTypeTitle)}>Policies Board</h2>

						<p className={css(styleSheet.description)}>Keep track of policies.</p>
					</section>

					<div className={css(styleSheet.cardsContainer)}>
						<div className={css(styleSheet.card)}>
							<Link to='/dataBoards/policies' className={css(styleSheet.boardLink)}>
								<div className={css(styleSheet.boardIconContainer, baseStyleSheet.bgBrightBlue)}>
									<PolicyIcon height={24} width={24} className={css(styleSheet.heartIcon)} />
								</div>

								<h6 className={css(styleSheet.boardTitle)}>Policies</h6>
							</Link>
						</div>
					</div>
				</div>
			) : null}

			{showMatters ? (
				<div className={css(styleSheet.cardsRow)}>
					<section className={css(styleSheet.boardRowHeader)}>
						<h2 className={css(styleSheet.boardTypeTitle)}>Matters Board</h2>

						<p className={css(styleSheet.description)}>Keep track of legal matters.</p>
					</section>

					<div className={css(styleSheet.cardsContainer)}>
						<div className={css(styleSheet.card)}>
							<Link to='/dataBoards/matters' className={css(styleSheet.boardLink)}>
								<div className={css(styleSheet.boardIconContainer, baseStyleSheet.bgGreen)}>
									<FolderIcon fillColor={white} className={css(styleSheet.heartIcon)} />
								</div>

								<h6 className={css(styleSheet.boardTitle)}>Matters</h6>
							</Link>
						</div>
					</div>
				</div>
			) : null}

			{showMeetings ? (
				<div className={css(styleSheet.cardsRow)}>
					<section className={css(styleSheet.boardRowHeader)}>
						<h2 className={css(styleSheet.boardTypeTitle)}>Meetings Board</h2>

						<p className={css(styleSheet.description)}>Browse and manage scheduled meetings.</p>
					</section>

					<div className={css(styleSheet.cardsContainer)}>
						<div className={css(styleSheet.card)}>
							<Link to='/dataBoards/meetings' className={css(styleSheet.boardLink)}>
								<div className={css(styleSheet.boardIconContainer, baseStyleSheet.bgOrange)}>
									<DataboardsIcon fill={white} className={css(styleSheet.boardIcon)} />
								</div>

								<h6 className={css(styleSheet.boardTitle)}>Meetings</h6>
							</Link>
						</div>
					</div>
				</div>
			) : null}

			{showOpportunities ? (
				<div className={css(styleSheet.cardsRow)}>
					<section className={css(styleSheet.boardRowHeader)}>
						<h2 className={css(styleSheet.boardTypeTitle)}>Opportunity Boards</h2>

						<p className={css(styleSheet.description)}>Track your opportunities from cold leads to raving clients.</p>
					</section>

					{(boardsQuery.data?.pages[0]?.totalCount ?? 0) > MIN_BOARDS_FOR_SEARCH ? (
						<Input
							type='text'
							placeholder='Search boards...'
							value={boardSearchTerm}
							onChange={e => setBoardSearchTerm(e.target.value)}
							styles={[styleSheet.boardSearchInput]}
						/>
					) : null}

					<div className={css(styleSheet.cardsContainer)}>
						{isLoadingOpportunityBoards ? <LoadingSpinner type='large' /> : null}

						{filteredBoards.map(board => {
							return (
								<div className={css(styleSheet.card)} key={`board-card-${board.id}`}>
									<div className={css(styleSheet.moreMenuContainer)}>
										<Dropdown>
											<DropdownEllipsisTrigger />
											<DropdownContent>
												<DropdownItem onClick={() => editBoard(board)}>Edit</DropdownItem>
												<DropdownItem onClick={() => deleteBoard(board)}>Delete</DropdownItem>
											</DropdownContent>
										</Dropdown>
									</div>

									<Link to={`/dataBoards/opportunities/${board.id}`} className={css(styleSheet.boardLink)}>
										<div className={css(styleSheet.boardIconContainer, styleSheet.greenBackground)}>
											<DataboardsIcon fill={white} className={css(styleSheet.boardIcon)} />
										</div>

										<h6 className={css(styleSheet.boardTitle)}>{board.name}</h6>
									</Link>
								</div>
							);
						})}

						<button className={css(styleSheet.newBoardCard)} onClick={createBoard}>
							<h6 className={css(styleSheet.newBoardTitle)}>+ Add Opportunity Board</h6>
						</button>
					</div>
				</div>
			) : null}

			<EditBoardModal
				board={boardToEdit}
				initialColumns={initialColumns}
				modalProps={{
					isOpen: isEditBoardOpen,
					onRequestClose: () => setIsEditBoardOpen(false),
				}}
				onEditBoard={onEditBoard}
				onCreateBoard={onCreateBoard}
			/>

			{boardToDelete ? (
				<DeleteConfirmation
					bodyText={`Are you sure you want to delete the ${boardToDelete.name} board?`}
					isOpen
					onFinish={onDeleteBoard}
				/>
			) : null}
		</Page>
	);
};
